import React from 'react'

import { ACCOUNT_ADMIN, TOKEN_BUYER } from 'roles'

import {
  ACCEPT_INVITATION,
  ACTIVATION_INTERSTITIAL,
  ADMINISTRATION,
  ASSESSMENT,
  GENERAL_ACCOUNT_INFORMATION,
  INVITATION,
  LINK_RELATIONSHIPS_PROFILE,
  LOGIN_FORGOT_PASSWORD,
  LOGIN_IMPERSONATE,
  LOGIN_RESET_PASSWORD,
  LOGIN_SSO_ERROR,
  LOGIN_SSO_PROVIDERS,
  LOGIN_SSO,
  LOGIN,
  MY_5_DYNAMICS_PROFILE,
  MY_5_DYNAMICS,
  NETWORK_DASHBOARD,
  NETWORK,
  NEWS,
  PROFILE_BY_CONTEXT,
  SETUP_RELATIONSHIPS_ACCOUNT,
  SIGNUP_SSO,
  SIGNUP,
  TEAMS_DASHBOARD,
  TEAMS_SPIDER_GRAPH,
  TEAMS,
  THIRD_PERSON_PROFILE,
  TOKEN_ADMINISTRATION,
  TRAINING_CENTER,
  TRAINING,
  WELCOME,
} from './urls'

// Alternate layouts — React.lazy so they load only when a route needs them.
const SimpleLayout = React.lazy(() => import('components/Application/components/layouts/SimpleLayout'))
const WelcomeLayout = React.lazy(() => import('components/Application/components/layouts/WelcomeLayout'))
const TrainingLayout = React.lazy(() => import('components/Application/components/layouts/TrainingLayout'))
const ProfileByContextLayout = React.lazy(
  () => import('components/Application/components/layouts/ProfileByContextLayout'),
)

// Lazy-loaded route components. Each React.lazy(() => import(...)) becomes its own
// webpack chunk. Suspense + RouteFallback live in Route.js inside the layout so
// header/footer stay visible while the page chunk loads.
const Assessment = React.lazy(() => import('containers/Assessment'))
const PageAcceptInvitation = React.lazy(() => import('components/PageAcceptInvitation'))
const PageActivationInterstitial = React.lazy(() => import('containers/PageActivationInterstitial'))
const PageAdministration = React.lazy(() => import('containers/PageAdministration'))
const PageAssessment = React.lazy(() => import('components/PageAssessment'))
const PageForgotPassword = React.lazy(() => import('containers/PageForgotPassword'))
const PageGeneralAccountInformation = React.lazy(() => import('containers/PageGeneralAccountInformation'))
const PageImpersonate = React.lazy(() => import('containers/PageImpersonate'))
const PageInvitation = React.lazy(() => import('containers/PageInvitation'))
const PageLinkRelationshipsProfile = React.lazy(() => import('containers/PageLinkRelationshipsProfile'))
const PageLogin = React.lazy(() => import('containers/PageLogin'))
const PageMy5Dynamics = React.lazy(() => import('components/PageMy5Dynamics'))
const PageMyProfile = React.lazy(() => import('containers/PageMyProfile'))
const PageNetwork = React.lazy(() => import('containers/PageNetwork'))
const PageNetworkDashboard = React.lazy(() => import('containers/PageNetworkDashboard'))
const PageNews = React.lazy(() => import('containers/PageNews'))
const PageNotFound = React.lazy(() => import('components/PageNotFound'))
const PageProfileByContext = React.lazy(() => import('components/PageProfileByContext'))
const PageProfileThirdPerson = React.lazy(() => import('containers/PageProfileThirdPerson'))
const PageRelationships = React.lazy(() => import('containers/PageRelationships'))
const PageResetPassword = React.lazy(() => import('containers/PageResetPassword'))
const PageSignUp = React.lazy(() => import('components/PageSignUp'))
const PageSignUpSSO = React.lazy(() => import('containers/PageSignUpSSO'))
const PageSpiderGraph = React.lazy(() => import('components/PageSpiderGraph'))
const PageSSOLogin = React.lazy(() => import('containers/PageSSOLogin'))
const PageSSOLoginError = React.lazy(() => import('containers/PageSSOLoginError'))
const PageSSOLoginProviders = React.lazy(() => import('containers/PageSSOLoginProviders'))
const PageTeams = React.lazy(() => import('containers/PageTeams'))
const PageTeamsDashboard = React.lazy(() => import('containers/PageTeamsDashboard'))
const PageTokenAdministration = React.lazy(() => import('containers/PageTokenAdministration'))
const PageTraining = React.lazy(() => import('containers/PageTraining'))
const PageTrainingCenter = React.lazy(() => import('containers/PageTrainingCenter'))

export const routes = [
  {
    component: Assessment,
    layout: SimpleLayout,
    path: ASSESSMENT,
    private: true,
  },
  {
    component: PageActivationInterstitial,
    exact: true,
    layout: SimpleLayout,
    path: ACTIVATION_INTERSTITIAL,
    private: true,
  },
  {
    component: PageAdministration,
    path: ADMINISTRATION,
    private: true,
    validatePersonalAccount: true,
  },
  {
    component: PageTokenAdministration,
    path: TOKEN_ADMINISTRATION,
    permissions: TOKEN_BUYER || ACCOUNT_ADMIN,
    private: true,
    validatePersonalAccount: true,
  },
  {
    component: PageResetPassword,
    path: LOGIN_RESET_PASSWORD,
  },
  {
    component: PageImpersonate,
    path: LOGIN_IMPERSONATE,
  },
  {
    component: PageForgotPassword,
    path: LOGIN_FORGOT_PASSWORD,
  },
  {
    component: PageSSOLogin,
    path: LOGIN_SSO,
  },
  {
    component: PageSSOLoginProviders,
    path: LOGIN_SSO_PROVIDERS,
  },
  {
    component: PageSSOLoginError,
    path: LOGIN_SSO_ERROR,
  },
  {
    component: PageGeneralAccountInformation,
    path: GENERAL_ACCOUNT_INFORMATION,
    private: true,
  },
  {
    component: PageInvitation,
    path: INVITATION,
  },
  {
    component: PageAcceptInvitation,
    path: ACCEPT_INVITATION,
  },
  {
    component: PageLinkRelationshipsProfile,
    path: LINK_RELATIONSHIPS_PROFILE,
  },
  {
    component: PageLogin,
    path: LOGIN,
  },
  {
    component: PageMy5Dynamics,
    path: MY_5_DYNAMICS,
    private: true,
  },
  {
    component: PageMyProfile,
    path: MY_5_DYNAMICS_PROFILE,
    private: true,
  },

  {
    component: PageNetwork,
    path: NETWORK,
    private: true,
    validatePersonalAccount: true,
  },
  {
    component: PageNetworkDashboard,
    path: NETWORK_DASHBOARD,
    private: true,
  },
  {
    component: PageProfileByContext,
    path: PROFILE_BY_CONTEXT,
    private: true,
    layout: ProfileByContextLayout,
  },
  {
    component: PageProfileThirdPerson,
    path: THIRD_PERSON_PROFILE,
    private: true,
  },
  {
    component: PageRelationships,
    path: SETUP_RELATIONSHIPS_ACCOUNT,
    private: true,
  },
  {
    component: PageSignUpSSO,
    path: SIGNUP_SSO,
  },
  {
    component: PageSignUp,
    path: SIGNUP,
  },
  {
    component: PageTeams,
    path: TEAMS,
    private: true,
    validatePersonalAccount: true,
  },
  {
    component: PageTeamsDashboard,
    path: TEAMS_DASHBOARD,
    private: true,
    validatePersonalAccount: true,
  },
  {
    component: PageSpiderGraph,
    path: TEAMS_SPIDER_GRAPH,
    private: true,
    validatePersonalAccount: true,
    layout: ProfileByContextLayout,
  },
  {
    component: PageTrainingCenter,
    layout: TrainingLayout,
    path: TRAINING_CENTER,
    private: true,
  },
  {
    component: PageTraining,
    layout: TrainingLayout,
    path: TRAINING,
    private: true,
  },
  {
    component: PageNews,
    path: NEWS,
    private: true,
  },
  {
    component: PageAssessment,
    layout: WelcomeLayout,
    path: WELCOME,
    private: true,
  },
  {
    component: PageNotFound,
    path: '*',
  },
]

export default routes
